import { DateTime } from 'luxon';
import TimeTaxSupportSystemClient from '../../../../../system';
import {
  AgrupadorCorporacionLiquidadora,
  AgrupadorCorporacionLiquidadoraTipo,
} from '../../../../../system/agrupador_corporacion_liquidadora/AgrupadorCorporacionLiquidadoraSystem';
import { ISelectableOption } from '@fluentui/react';
import { LiquidacionInsumo } from '../../../../../system/settlement/aje/liquidacion-insumos';

export const validateRegistroActividades = async ({
  index,
  periodo,
  system,
  settlementType,
  setItems,
  setItemLoading,
  updateItemByIndex,
}: {
  index: number;
  periodo: DateTime;
  system: TimeTaxSupportSystemClient;
  settlementType: ISelectableOption<AgrupadorCorporacionLiquidadora>;
  setItems: React.Dispatch<React.SetStateAction<LiquidacionInsumo[]>>;
  setItemLoading: (setItems: React.Dispatch<React.SetStateAction<LiquidacionInsumo[]>>, index: number, loading: boolean) => void;
  updateItemByIndex: (items: LiquidacionInsumo[], index: number, updatedItem: Partial<LiquidacionInsumo>) => LiquidacionInsumo[];
}) => {
  try {
    setItemLoading(setItems, index, true);

    const tipoAgrupador = settlementType.data!.agrcorliq_tipo;

    const data = await system.getLiquidacionAsincronaV2System().getActividadInRange(periodo, tipoAgrupador);

    const messages: Record<AgrupadorCorporacionLiquidadoraTipo, string> = {
      [AgrupadorCorporacionLiquidadoraTipo.AJE_FILMS]: 'No hay datos de actividades en el periodo seleccionado',
      [AgrupadorCorporacionLiquidadoraTipo.ASESORIAS]: 'No hay datos de actividades en el periodo seleccionado',
    };

    const hasData = Array.isArray(data) && data.length > 0;
    const message = messages[tipoAgrupador] ?? 'No hay datos disponibles';

    setItems((prev) =>
      updateItemByIndex(prev, index, {
        valida: hasData,
        validacion: hasData ? 'Insumo existente' : message,
      }),
    );
  } catch {
    setItems((prev) =>
      updateItemByIndex(prev, index, {
        valida: false,
        validacion: 'Error al validar el registro de actividades',
      }),
    );
  } finally {
    setItemLoading(setItems, index, false);
  }
};

export const validateCostoHora = async ({
  index,
  periodo,
  system,
  settlementType,
  setItems,
  setItemLoading,
  updateItemByIndex,
}: {
  index: number;
  periodo: DateTime;
  system: TimeTaxSupportSystemClient;
  settlementType: ISelectableOption<AgrupadorCorporacionLiquidadora>;
  setItems: React.Dispatch<React.SetStateAction<LiquidacionInsumo[]>>;
  setItemLoading: (setItems: React.Dispatch<React.SetStateAction<LiquidacionInsumo[]>>, index: number, loading: boolean) => void;
  updateItemByIndex: (items: LiquidacionInsumo[], index: number, updatedItem: Partial<LiquidacionInsumo>) => LiquidacionInsumo[];
}) => {
  try {
    setItemLoading(setItems, index, true);

    const data = await system.getLiquidacionAsincronaV2System().getRemunInActividad(periodo, settlementType.data!.agrcorliq_tipo);

    let validacion = '';

    switch (data.status) {
      case 'NO_ACTIVITIES':
      case 'NONE_RENUMERATED':
        validacion = 'No hay datos de remuneraci�n para ning�n colaborador en el periodo seleccionado';
        break;

      case 'PARTIAL_RENUMERATED':
        validacion = 'Hay colaboradores que no tienen datos de remuneraci�n';
        break;

      case 'ALL_RENUMERATED':
        validacion = 'Insumo existente';
        break;
    }

    setItems((prev) =>
      updateItemByIndex(prev, index, {
        valida: data.status === 'ALL_RENUMERATED',
        validacion,
      }),
    );
  } catch {
    setItems((prev) =>
      updateItemByIndex(prev, index, {
        valida: false,
        validacion: 'Error al validar la remuneraci�n',
      }),
    );
  } finally {
    setItemLoading(setItems, index, false);
  }
};

export const validateSubDiario = ({
  index,
  items,
  setItems,
  setItemLoading,
  updateItemByIndex,
}: {
  index: number;
  items: LiquidacionInsumo[];
  setItems: React.Dispatch<React.SetStateAction<LiquidacionInsumo[]>>;
  setItemLoading: (setItems: React.Dispatch<React.SetStateAction<LiquidacionInsumo[]>>, index: number, loading: boolean) => void;
  updateItemByIndex: (items: LiquidacionInsumo[], index: number, updatedItem: Partial<LiquidacionInsumo>) => LiquidacionInsumo[];
}) => {
  setItemLoading(setItems, index, true);

  const hasFile = !!items[index].liqins_id;

  setItems((prev) =>
    updateItemByIndex(prev, index, {
      valida: hasFile,
      validacion: hasFile ? 'Insumo existente' : 'Insumo no existente',
    }),
  );

  setItemLoading(setItems, index, false);
};

// Helper
const getFileByRegex = (files: any, regex: RegExp): string | null => {
  const key = Object.keys(files).find((k) => regex.test(k));
  return key ? files[key] : null;
};

type AjeFilmsProjectsIndices = {
  detallado: number;
  resumen: number;
  resumenXlsx: number;
};

export const validateAjeFilmsProjects = async ({
  indices,
  periodo,
  system,
  setItems,
  setItemLoading,
  updateItemByIndex,
}: {
  indices: AjeFilmsProjectsIndices;
  periodo: DateTime;
  system: TimeTaxSupportSystemClient;
  setItems: React.Dispatch<React.SetStateAction<LiquidacionInsumo[]>>;
  setItemLoading: (setItems: React.Dispatch<React.SetStateAction<LiquidacionInsumo[]>>, index: number, loading: boolean) => void;
  updateItemByIndex: (items: LiquidacionInsumo[], index: number, updatedItem: Partial<LiquidacionInsumo>) => LiquidacionInsumo[];
}) => {
  const periodoYYYYMM = periodo.toFormat('yyyyLL');

  const allIndices = [indices.detallado, indices.resumen, indices.resumenXlsx].filter((i) => i !== -1);

  allIndices.forEach((idx) => setItemLoading(setItems, idx, true));

  try {
    const response = await system.getLiquidacionAsincronaV2System().getReporteAceptadoAjeFilmsProjects(periodoYYYYMM);

    const files = {
      detallado: getFileByRegex(response?.files ?? {}, /^reporte_detallado_.*\.parquet$/),
      resumen: getFileByRegex(response?.files ?? {}, /^informe_horas_ajefilms_proyectos_.*\.parquet$/),
      resumenXlsx: getFileByRegex(response?.files ?? {}, /^informe_horas_ajefilms_proyectos_.*\.xlsx$/),
    };

    const applyValidation = (index: number, path: string | null) => {
      if (index === -1) return;

      setItems((prev) =>
        updateItemByIndex(prev, index, {
          valida: !!path,
          validacion: path ? 'Insumo existente' : 'No se encontr� reporte aceptado',
          liqins_path: path,
        }),
      );
    };

    applyValidation(indices.detallado, files.detallado);
    applyValidation(indices.resumen, files.resumen);
    applyValidation(indices.resumenXlsx, files.resumenXlsx);
  } catch (error) {
    console.log(error);

    allIndices.forEach((idx) => {
      setItems((prev) =>
        updateItemByIndex(prev, idx, {
          valida: false,
          validacion: 'Error al validar reporte aceptado',
        }),
      );
    });
  } finally {
    allIndices.forEach((idx) => setItemLoading(setItems, idx, false));
  }
};

type AsesoriaIndices = {
  detallado: number;
  resumen: number;
  resumenXlsx: number;
};

export const validateAsesorias = async ({
  peru,
  espana,
  ajefilms,
  periodo,
  system,
  setItems,
  setItemLoading,
  updateItemByIndex,
}: {
  peru: AsesoriaIndices;
  espana: AsesoriaIndices;
  ajefilms: AsesoriaIndices;
  periodo: DateTime;
  system: TimeTaxSupportSystemClient;
  setItems: React.Dispatch<React.SetStateAction<LiquidacionInsumo[]>>;
  setItemLoading: (setItems: React.Dispatch<React.SetStateAction<LiquidacionInsumo[]>>, index: number, loading: boolean) => void;
  updateItemByIndex: (items: LiquidacionInsumo[], index: number, updatedItem: Partial<LiquidacionInsumo>) => LiquidacionInsumo[];
}) => {
  const periodoYYYYMM = periodo.toFormat('yyyyLL');

  const allIndices = [
    peru.detallado,
    peru.resumen,
    peru.resumenXlsx,
    espana.detallado,
    espana.resumen,
    espana.resumenXlsx,
    ajefilms.detallado,
    ajefilms.resumen,
    ajefilms.resumenXlsx,
  ].filter((i) => i !== -1);

  allIndices.forEach((idx) => setItemLoading(setItems, idx, true));

  try {
    const [peruRes, espanaRes, ajefilmsRes] = await Promise.all([
      system.getLiquidacionAsincronaV2System().getReporteAceptadoAsesoriasPe(periodoYYYYMM),
      system.getLiquidacionAsincronaV2System().getReporteAceptadoAsesoriasEs(periodoYYYYMM),
      system.getLiquidacionAsincronaV2System().getReporteAceptadoAjeFilmsServices(periodoYYYYMM),
    ]);

    const peruFiles = {
      detallado: getFileByRegex(peruRes?.files ?? {}, /^reporte_detallado_.*\.parquet$/),
      resumen: getFileByRegex(peruRes?.files ?? {}, /^informe_horas_pe_.*\.parquet$/),
      resumenXlsx: getFileByRegex(peruRes?.files ?? {}, /^informe_horas_pe_.*\.xlsx$/),
    };

    const espanaFiles = {
      detallado: getFileByRegex(espanaRes?.files ?? {}, /^reporte_detallado_.*\.parquet$/),
      resumen: getFileByRegex(espanaRes?.files ?? {}, /^informe_horas_es_.*\.parquet$/),
      resumenXlsx: getFileByRegex(espanaRes?.files ?? {}, /^informe_horas_es_.*\.xlsx$/),
    };

    const ajefilmsFiles = {
      detallado: getFileByRegex(ajefilmsRes?.files ?? {}, /^reporte_detallado_.*\.parquet$/),
      resumen: getFileByRegex(ajefilmsRes?.files ?? {}, /^informe_horas_ajefilms_.*\.parquet$/),
      resumenXlsx: getFileByRegex(ajefilmsRes?.files ?? {}, /^informe_horas_ajefilms_.*\.xlsx$/),
    };

    const applyValidation = (index: number, path: string | null) => {
      if (index === -1) return;

      setItems((prev) =>
        updateItemByIndex(prev, index, {
          valida: !!path,
          validacion: path ? 'Insumo existente' : 'No se encontr� reporte aceptado',
          liqins_path: path,
        }),
      );
    };

    // Per�
    applyValidation(peru.detallado, peruFiles.detallado);
    applyValidation(peru.resumen, peruFiles.resumen);
    applyValidation(peru.resumenXlsx, peruFiles.resumenXlsx);

    // Espa�a
    applyValidation(espana.detallado, espanaFiles.detallado);
    applyValidation(espana.resumen, espanaFiles.resumen);
    applyValidation(espana.resumenXlsx, espanaFiles.resumenXlsx);

    // AJE Films
    applyValidation(ajefilms.detallado, ajefilmsFiles.detallado);
    applyValidation(ajefilms.resumen, ajefilmsFiles.resumen);
    applyValidation(ajefilms.resumenXlsx, ajefilmsFiles.resumenXlsx);
  } catch (error) {
    console.log(error);

    allIndices.forEach((idx) => {
      setItems((prev) =>
        updateItemByIndex(prev, idx, {
          valida: false,
          validacion: 'Error al validar reporte aceptado',
        }),
      );
    });
  } finally {
    allIndices.forEach((idx) => setItemLoading(setItems, idx, false));
  }
};

export const validateFile = ({
  index,
  items,
  setItems,
  setItemLoading,
  updateItemByIndex,
}: {
  index: number;
  items: LiquidacionInsumo[];
  setItems: React.Dispatch<React.SetStateAction<LiquidacionInsumo[]>>;
  setItemLoading: (setItems: React.Dispatch<React.SetStateAction<LiquidacionInsumo[]>>, index: number, loading: boolean) => void;
  updateItemByIndex: (items: LiquidacionInsumo[], index: number, updatedItem: Partial<LiquidacionInsumo>) => LiquidacionInsumo[];
}) => {
  setItemLoading(setItems, index, true);

  const hasFile = !!items[index].liqins_id;

  setItems((prev) =>
    updateItemByIndex(prev, index, {
      valida: hasFile,
      validacion: hasFile ? 'Insumo existente' : 'Insumo no existente',
    }),
  );

  setItemLoading(setItems, index, false);
};

export const validateLiquidacionAjeFilms = async ({
  index,
  periodo,
  system,
  settlementType,
  setItems,
  setItemLoading,
  updateItemByIndex,
}: {
  index: number;
  periodo: DateTime;
  system: TimeTaxSupportSystemClient;
  settlementType: ISelectableOption<AgrupadorCorporacionLiquidadora>;
  setItems: React.Dispatch<React.SetStateAction<LiquidacionInsumo[]>>;
  setItemLoading: (setItems: React.Dispatch<React.SetStateAction<LiquidacionInsumo[]>>, index: number, loading: boolean) => void;
  updateItemByIndex: (items: LiquidacionInsumo[], index: number, updatedItem: Partial<LiquidacionInsumo>) => LiquidacionInsumo[];
}) => {
  try {
    setItemLoading(setItems, index, true);

    const exist = await system.getLiquidacionAsincronaV2System().getLiquidationExistsAjeFilms(periodo);

    setItems((prev) =>
      updateItemByIndex(prev, index, {
        valida: exist,
        validacion: exist ? 'Liquidacion existente' : 'No existe una liquidacion aceptada para el periodo seleccionado',
      }),
    );
  } catch {
    setItems((prev) =>
      updateItemByIndex(prev, index, {
        valida: false,
        validacion: 'Error al validar el insumo',
      }),
    );
  } finally {
    setItemLoading(setItems, index, false);
  }
};

export const validateInsumoGastosBolsita = async ({
  index,
  system,
  setItems,
  setItemLoading,
  updateItemByIndex,
}: {
  index: number;
  system: TimeTaxSupportSystemClient;
  setItems: React.Dispatch<React.SetStateAction<LiquidacionInsumo[]>>;
  setItemLoading: (setItems: React.Dispatch<React.SetStateAction<LiquidacionInsumo[]>>, index: number, loading: boolean) => void;
  updateItemByIndex: (items: LiquidacionInsumo[], index: number, updatedItem: Partial<LiquidacionInsumo>) => LiquidacionInsumo[];
}) => {
  try {
    setItemLoading(setItems, index, true);

    const exist = await system.getSettlementSystem().getExistDistribucionManualGastosBolsita();

    setItems((prev) =>
      updateItemByIndex(prev, index, {
        valida: exist,
        validacion: exist ? 'Insumo existente' : 'Insumo no existente',
      }),
    );
  } catch {
    setItems((prev) =>
      updateItemByIndex(prev, index, {
        valida: false,
        validacion: 'Error al validar el insumo',
      }),
    );
  } finally {
    setItemLoading(setItems, index, false);
  }
};

export const validateInsumoDerivacionDeCostos = async ({
  index,
  system,
  setItems,
  setItemLoading,
  updateItemByIndex,
}: {
  index: number;
  system: TimeTaxSupportSystemClient;
  setItems: React.Dispatch<React.SetStateAction<LiquidacionInsumo[]>>;
  setItemLoading: (setItems: React.Dispatch<React.SetStateAction<LiquidacionInsumo[]>>, index: number, loading: boolean) => void;
  updateItemByIndex: (items: LiquidacionInsumo[], index: number, updatedItem: Partial<LiquidacionInsumo>) => LiquidacionInsumo[];
}) => {
  try {
    setItemLoading(setItems, index, true);

    const exist = await system.getSettlementSystem().getExistPlantillaDerivacionCostos();

    setItems((prev) =>
      updateItemByIndex(prev, index, {
        valida: exist,
        validacion: exist ? 'Insumo existente' : 'Insumo no existente',
      }),
    );
  } catch {
    setItems((prev) =>
      updateItemByIndex(prev, index, {
        valida: false,
        validacion: 'Error al validar el insumo',
      }),
    );
  } finally {
    setItemLoading(setItems, index, false);
  }
};
